import React, { useMemo } from 'react';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { groupItemsByDate } from '../../utils/date';
import { PurchaseOrderFilters } from './components/PurchaseOrderFilters';
import { PurchaseOrderHeader } from './components/PurchaseOrderHeader';
import { PurchaseOrderMobileList } from './components/PurchaseOrderMobileList';
import { PurchaseOrderModals } from './components/PurchaseOrderModals';
import { PurchaseOrderTable } from './components/PurchaseOrderTable';
import { usePurchaseOrderListState } from './hooks/usePurchaseOrderListState';
import { usePermissions } from '../../hooks/usePermissions';
import { useLanguage } from '../../features/language/useLanguage';
import { Pagination } from '../../components/common/Pagination';

export const PurchaseOrderList: React.FC = () => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('purchase_orders', 'write');
  const {
    currencySymbol,
    navigate,
    loading,
    error,
    pos,
    editingPO,
    setEditingPO,
    searchFilter,
    setSearchFilter,
    statusFilter,
    setStatusFilter,
    orderStartDateFilter,
    setOrderStartDateFilter,
    orderEndDateFilter,
    setOrderEndDateFilter,
    deliveryStartDateFilter,
    setDeliveryStartDateFilter,
    deliveryEndDateFilter,
    setDeliveryEndDateFilter,
    paymentStatusFilter,
    setPaymentStatusFilter,
    deliveryStatusFilter,
    setDeliveryStatusFilter,
    viewingSupplier,
    setViewingSupplier,
    isSupplierModalOpen,
    setIsSupplierModalOpen,
    expandedRowIds,
    toggleRow,
    receivingPO,
    setReceivingPO,
    payingPO,
    setPayingPO,
    profileModalData,
    setProfileModalData,
    greenBeanProfiles,
    handleProfileClick,
    handleUpdatePO,
    handleSupplierClick,
    filteredPOs,
    totalPOs,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortConfig,
    handleSort,
    shouldGroup,
    groupedPOs,
    sortedDates,
    handleReceiveSubmit,
    handlePaySubmit,
  } = usePurchaseOrderListState();

  const mobileGrouped = useMemo(() => groupItemsByDate(filteredPOs, po => po.orderDate), [filteredPOs]);
  const mobileDates = useMemo(() => Object.keys(mobileGrouped).sort((a, b) => b.localeCompare(a)), [mobileGrouped]);

  if (loading && pos.length === 0) {
    return <LoadingSpinner text={t('purchaseOrder.table.loading', undefined, 'Loading purchase orders...')} />;
  }

  return (
    <div className="space-y-6" data-test-id="purchase-order-list">
      <PurchaseOrderHeader 
        canWrite={canWrite}
        onCreate={() => navigate('/purchase-orders/create')} 
      />

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-inset ring-red-600/20" data-test-id="po-error">
          {error}
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <PurchaseOrderFilters
          searchFilter={searchFilter}
          onSearchFilterChange={setSearchFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          orderStartDateFilter={orderStartDateFilter}
          onOrderStartDateFilterChange={setOrderStartDateFilter}
          orderEndDateFilter={orderEndDateFilter}
          onOrderEndDateFilterChange={setOrderEndDateFilter}
          deliveryStartDateFilter={deliveryStartDateFilter}
          onDeliveryStartDateFilterChange={setDeliveryStartDateFilter}
          deliveryEndDateFilter={deliveryEndDateFilter}
          onDeliveryEndDateFilterChange={setDeliveryEndDateFilter}
          paymentStatusFilter={paymentStatusFilter}
          onPaymentStatusFilterChange={setPaymentStatusFilter}
          deliveryStatusFilter={deliveryStatusFilter}
          onDeliveryStatusFilterChange={setDeliveryStatusFilter}
        />

        {filteredPOs.length === 0 ? (
          <div className="px-4 py-5 sm:p-6 text-center text-gray-500" data-test-id="po-empty">
            {t('purchaseOrder.table.empty')}
          </div>
        ) : (
          <>
            <PurchaseOrderMobileList
              canWrite={canWrite}
              sortedDates={shouldGroup ? sortedDates : mobileDates}
              groupedPOs={shouldGroup ? groupedPOs : mobileGrouped}
              currencySymbol={currencySymbol}
              onEdit={setEditingPO}
              onSupplierClick={handleSupplierClick}
              onPay={setPayingPO}
              onReceive={setReceivingPO}
            />
            <PurchaseOrderTable
              canWrite={canWrite}
              filteredPOs={filteredPOs}
              shouldGroup={shouldGroup}
              groupedPOs={groupedPOs}
              sortedDates={sortedDates}
              sortConfig={sortConfig}
              onSort={handleSort}
              currencySymbol={currencySymbol}
              expandedRowIds={expandedRowIds}
              onToggleRow={toggleRow}
              onEdit={setEditingPO}
              onSupplierClick={handleSupplierClick}
              onPay={setPayingPO}
              onReceive={setReceivingPO}
              onProfileClick={handleProfileClick}
              greenBeanProfiles={greenBeanProfiles}
            />
          </>
        )}
        <Pagination page={page} pageSize={pageSize} total={totalPOs} onPageChange={setPage} onPageSizeChange={setPageSize} isLoading={loading} />
      </div>

      <PurchaseOrderModals
        editingPO={editingPO}
        onCloseEdit={() => setEditingPO(null)}
        onUpdatePO={handleUpdatePO}
        viewingSupplier={viewingSupplier}
        isSupplierModalOpen={isSupplierModalOpen}
        onCloseSupplier={() => {
          setIsSupplierModalOpen(false);
          setViewingSupplier(null);
        }}
        profileModalData={profileModalData}
        onCloseProfile={() => setProfileModalData({ isOpen: false })}
        onProfileSuccess={() => setProfileModalData({ isOpen: false })}
        receivingPO={receivingPO}
        onCloseReceive={() => setReceivingPO(null)}
        onReceiveSubmit={handleReceiveSubmit}
        payingPO={payingPO}
        onClosePay={() => setPayingPO(null)}
        onPaySubmit={handlePaySubmit}
        readOnly={!canWrite}
      />
    </div>
  );
};
